import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { FormikProps } from 'formik';
import { Alert } from '@mui/material';
import OvTextField from '../atoms/OvTextField';
import {
  PLACEMENT_DESCRIPTION_MAX_LENGTH,
  PLACEMENT_KEY_EXAMPLE,
  PLACEMENT_KEY_MAX_LENGTH,
} from '../../../common/model/dto/video/video-constants';
import {
  NewPlacementValues,
  PlacementDescriptionError,
  PlacementKeyError,
} from '../../../common/model/type/video-placement.types';

interface OvAddVideoPlacementFormProps {
  formik: FormikProps<NewPlacementValues>;
  keyError: PlacementKeyError | null;
  descriptionError: PlacementDescriptionError | null;
}

const LIMITS = {
  max: PLACEMENT_KEY_MAX_LENGTH,
  maxDescription: PLACEMENT_DESCRIPTION_MAX_LENGTH,
};

const OvAddVideoPlacementForm: FC<OvAddVideoPlacementFormProps> = ({
  formik,
  keyError,
  descriptionError,
}) => {
  const { t } = useTranslation();
  const { key, description } = formik.values;

  const showKeyError = !!keyError && (!!key || formik.touched.key);
  const showDescriptionError =
    !!descriptionError && (!!description || formik.touched.description);

  return (
    <>
      <OvTextField
        id="new-placement-key"
        label={t('videos.placements.fields.key')}
        fullWidth
        autoFocus
        autoComplete="off"
        error={showKeyError}
        helperText={
          showKeyError
            ? t(`videos.placements.keyErrors.${keyError}`, LIMITS)
            : t('videos.placements.add.keyHint', {
                example: PLACEMENT_KEY_EXAMPLE,
              })
        }
        inputProps={{ spellCheck: false, style: { fontFamily: 'monospace' } }}
        {...formik.getFieldProps('key')}
      />
      <OvTextField
        id="new-placement-description"
        label={t('videos.placements.fields.description')}
        fullWidth
        autoComplete="off"
        error={showDescriptionError}
        helperText={
          showDescriptionError
            ? t(
                `videos.placements.descriptionErrors.${descriptionError}`,
                LIMITS
              )
            : t('videos.placements.add.descriptionHint')
        }
        {...formik.getFieldProps('description')}
      />
      <Alert severity="warning">{t('videos.placements.add.keyIsFinal')}</Alert>
    </>
  );
};

export default OvAddVideoPlacementForm;
